/**
 * Global Modal Backdrop Manager
 *
 * Manages a single shared backdrop for all modals to prevent:
 * - Overlays stacking and becoming darker
 * - Closing one modal removing backdrops for other open modals
 */

import { ref } from "vue";

const openModalCount = ref(0);
let backdropElement = null;
let initialized = false;

function ensureBackdropElement() {
    if (backdropElement) return backdropElement;

    backdropElement = document.getElementById("global-modal-backdrop");
    if (!backdropElement) {
        backdropElement = document.createElement("div");
        backdropElement.id = "global-modal-backdrop";
        backdropElement.className = "global-modal-backdrop";
        document.body.appendChild(backdropElement);
    }
    return backdropElement;
}

function showBackdrop() {
    const el = ensureBackdropElement();
    el.classList.add("show");
    document.body.classList.add("modal-open");
}

function hideBackdrop() {
    if (backdropElement) {
        backdropElement.classList.remove("show");
    }
    document.body.classList.remove("modal-open");
    document.body.style.overflow = "";
    document.body.style.paddingRight = "";
}

function removeBootstrapBackdrops() {
    document.querySelectorAll(".modal-backdrop").forEach((el) => el.remove());
}

/**
 * Check if any modal is currently visible in the DOM.
 * Uses multiple detection methods for reliability.
 */
export function hasVisibleModals() {
    // Check for modals with .show class
    const showModals = document.querySelectorAll(".modal.show");
    if (showModals.length > 0) return true;

    // Also check for modals with display: block (backup)
    const allModals = document.querySelectorAll(".modal");
    for (const modal of allModals) {
        const style = window.getComputedStyle(modal);
        if (style.display === "block" && style.visibility !== "hidden") {
            return true;
        }
    }

    return false;
}

/**
 * Sync backdrop state with actual visible modals
 */
export function syncBackdropState() {
    removeBootstrapBackdrops();

    if (hasVisibleModals()) {
        showBackdrop();
    } else {
        hideBackdrop();
    }
}

export function registerModal() {
    openModalCount.value++;
    showBackdrop();
    removeBootstrapBackdrops();
}

export function unregisterModal() {
    openModalCount.value = Math.max(0, openModalCount.value - 1);
    // Always sync with actual DOM state
    syncBackdropState();
}

export function getOpenModalCount() {
    return openModalCount.value;
}

/**
 * Initialize global Bootstrap modal event listeners.
 * Call this once in your app initialization (e.g., app.js).
 * This automatically handles backdrop for all Bootstrap modals.
 */
export function initGlobalModalBackdrop() {
    if (initialized || typeof document === "undefined") return;
    initialized = true;

    document.addEventListener("show.bs.modal", () => {
        registerModal();
    });

    document.addEventListener("shown.bs.modal", () => {
        removeBootstrapBackdrops();
    });

    document.addEventListener("hidden.bs.modal", () => {
        // Sync state after Bootstrap finishes
        setTimeout(syncBackdropState, 50);
    });

    // Handle hide event as early cleanup
    document.addEventListener("hide.bs.modal", () => {
        removeBootstrapBackdrops();
    });

    // Fallback: also sync when clicking the global backdrop
    ensureBackdropElement().addEventListener("click", () => {
        // Find all visible modals and trigger their close
        document.querySelectorAll(".modal.show").forEach((modal) => {
            const instance = bootstrap.Modal.getInstance(modal);
            if (instance) {
                instance.hide();
            }
        });
        // Force sync after a delay
        setTimeout(syncBackdropState, 100);
    });

    // MutationObserver fallback: watch for modal class changes
    const observer = new MutationObserver((mutations) => {
        let shouldSync = false;
        for (const mutation of mutations) {
            if (
                mutation.type === "attributes" &&
                mutation.attributeName === "class"
            ) {
                const target = mutation.target;
                if (target.classList.contains("modal")) {
                    shouldSync = true;
                    break;
                }
            }
            if (mutation.type === "childList") {
                // Modal added or removed from DOM
                for (const node of mutation.removedNodes) {
                    if (
                        node.nodeType === 1 &&
                        node.classList?.contains("modal")
                    ) {
                        shouldSync = true;
                        break;
                    }
                }
            }
        }
        if (shouldSync) {
            setTimeout(syncBackdropState, 50);
        }
    });

    observer.observe(document.body, {
        attributes: true,
        attributeFilter: ["class", "style"],
        childList: true,
        subtree: true,
    });
}

export default function useModalBackdrop() {
    return {
        hasVisibleModals,
        registerModal,
        unregisterModal,
        openModalCount,
        initGlobalModalBackdrop,
        syncBackdropState,
    };
}
